import React from 'react';
import { PieChart as PieChartIcon } from 'lucide-react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

import { EmptyState } from '@/components/shared/EmptyState';
import { cn } from '@/lib/utils';

import { chartColorList, useChartColors } from './useChartColors';

export interface DonutChartDatum {
    label: string;
    value: number;
}

export interface DonutChartProps {
    data: DonutChartDatum[];
    /** Shown under the total in the centre of the donut. */
    centerLabel: string;
    /** Usually the sum of all segments. */
    centerValue: React.ReactNode;
    height?: number;
    className?: string;
    formatValue?: (value: number) => string;
    emptyTitle?: string;
    emptyDescription?: string;
}

/**
 * A donut chart with a centred total, for status breakdowns (e.g. KYC
 * pending/approved/rejected). Themed via the `--chart-*` tokens through
 * `useChartColors` rather than a hard-coded hex palette.
 */
export const DonutChart: React.FC<DonutChartProps> = ({
    data,
    centerLabel,
    centerValue,
    height = 200,
    className,
    formatValue = value => String(value),
    emptyTitle = 'No data yet',
    emptyDescription,
}) => {
    const colors = chartColorList(useChartColors());
    const total = data.reduce((sum, item) => sum + item.value, 0);

    if (total <= 0) {
        return (
            <EmptyState
                icon={PieChartIcon}
                title={emptyTitle}
                description={emptyDescription}
                size="sm"
                className={className}
            />
        );
    }

    const summary = `${centerLabel}: ${centerValue}. ${data
        .map(item => `${item.label}: ${formatValue(item.value)}`)
        .join(', ')}`;

    return (
        <div className={cn('w-full', className)}>
            <div
                className="relative"
                role="img"
                aria-label={summary}
                style={{ width: '100%', height }}
            >
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={data}
                            dataKey="value"
                            nameKey="label"
                            innerRadius="65%"
                            outerRadius="100%"
                            paddingAngle={2}
                            stroke="none"
                        >
                            {data.map((entry, index) => (
                                <Cell key={entry.label} fill={colors[index % colors.length]} />
                            ))}
                        </Pie>
                        <Tooltip formatter={(value: number) => formatValue(value)} />
                    </PieChart>
                </ResponsiveContainer>
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-2xl font-semibold tabular-figures text-foreground">
                        {centerValue}
                    </span>
                    <span className="text-xs text-muted-foreground">{centerLabel}</span>
                </div>
            </div>

            <ul className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
                {data.map((entry, index) => (
                    <li
                        key={entry.label}
                        className="flex items-center gap-1.5 text-xs text-muted-foreground"
                    >
                        <span
                            aria-hidden="true"
                            className="h-2 w-2 shrink-0 rounded-full"
                            style={{ backgroundColor: colors[index % colors.length] }}
                        />
                        {entry.label}:{' '}
                        <span className="font-medium text-foreground">
                            {formatValue(entry.value)}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default DonutChart;
